import { create } from 'zustand'
import { auth } from '@/lib/cloudbase'

const USER_STORAGE_KEY = 'biging-auth-user'

export interface AuthUser {
  uid: string
  phone: string
  displayName?: string
  tenantCode: string
}

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (phone: string, password: string, tenantCode: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

function saveUser(user: AuthUser | null) {
  if (user) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
  } else {
    localStorage.removeItem(USER_STORAGE_KEY)
  }
}

function loadUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// 用缓存用户初始化，避免已登录用户看到 loading 闪烁
const cachedUser = loadUser()

export const useAuthStore = create<AuthState>((set, get) => ({
  user: cachedUser,
  isAuthenticated: !!cachedUser,
  isLoading: !cachedUser,

  login: async (phone: string, password: string, tenantCode: string) => {
    const { data, error } = await auth.signInWithPassword({ phone, password })
    if (error) {
      throw new Error(error.message || '登录失败')
    }

    const currentUser = data.user || auth.currentUser
    const user: AuthUser = {
      uid: (currentUser as any)?.uid || '',
      phone,
      displayName: (currentUser as any)?.customUserId || phone,
      tenantCode,
    }
    saveUser(user)
    set({ user, isAuthenticated: true, isLoading: false })
  },

  logout: async () => {
    await auth.signOut()
    saveUser(null)
    set({ user: null, isAuthenticated: false })
  },

  checkAuth: async () => {
    // 已认证时后台静默验证，不触发 loading 状态变更，避免子组件卸载/挂载
    const alreadyAuthenticated = get().isAuthenticated
    if (!alreadyAuthenticated) {
      set({ isLoading: true })
    }
    try {
      const { data, error } = await auth.getSession()
      if (!error && data.session) {
        const cached = loadUser()
        const user: AuthUser = cached || {
          uid: (data.user as any)?.uid || (data.session as any).sub || '',
          phone: '',
          displayName: (data.user as any)?.customUserId || '',
          tenantCode: '',
        }
        set({ user, isAuthenticated: true, isLoading: false })
      } else {
        saveUser(null)
        set({ user: null, isAuthenticated: false, isLoading: false })
      }
    } catch {
      saveUser(null)
      set({ user: null, isAuthenticated: false, isLoading: false })
    }
  },
}))
